/**
 * Columns Component for Puck Editor
 * 
 * Layout dengan DropZone untuk nested components
 */

import type { ComponentConfig } from '@measured/puck'
import { DropZone } from '@measured/puck'

export interface ColumnsProps {
  columns: 2 | 3 | 4
  gap: 'small' | 'medium' | 'large'
  distribution: 'equal' | 'left-heavy' | 'right-heavy'
  stackOnMobile: boolean
}

const gapClasses = {
  small: 'gap-4',
  medium: 'gap-6',
  large: 'gap-8',
}

// Static mapping to ensure Tailwind includes these classes in build
const mobileGridClasses = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
}

const getGridClasses = (columns: number, stackOnMobile: boolean) => {
  const mobileClass = stackOnMobile 
    ? 'grid-cols-1' 
    : (mobileGridClasses[columns as keyof typeof mobileGridClasses] ?? 'grid-cols-2')
  switch (columns) {
    case 2:
      return `${mobileClass} md:grid-cols-2`
    case 3:
      return `${mobileClass} md:grid-cols-3`
    case 4:
      return `${mobileClass} md:grid-cols-2 lg:grid-cols-4`
    default:
      return `${mobileClass} md:grid-cols-2`
  }
}

export function Columns({ columns, gap, distribution, stackOnMobile }: ColumnsProps) {
  const columnArray = Array.from({ length: columns }, (_, i) => i)

  // For non-equal distributions with 2 columns
  const getColSpan = (index: number) => {
    if (distribution === 'equal' || columns !== 2) return ''
    
    const isFirstColumn = index === 0
    const isLeftHeavy = distribution === 'left-heavy'
    
    // Desktop classes
    const desktopSpan = isFirstColumn
      ? (isLeftHeavy ? 'md:col-span-2' : 'md:col-span-1')
      : (isLeftHeavy ? 'md:col-span-1' : 'md:col-span-2')
    
    // Mobile classes when not stacking
    if (!stackOnMobile) {
      const mobileSpan = isFirstColumn
        ? (isLeftHeavy ? 'col-span-2' : 'col-span-1')
        : (isLeftHeavy ? 'col-span-1' : 'col-span-2')
      return `${mobileSpan} ${desktopSpan}`
    }
    
    return desktopSpan
  }

  const gridClass =
    distribution !== 'equal' && columns === 2
      ? `${stackOnMobile ? 'grid-cols-1' : 'grid-cols-3'} md:grid-cols-3`
      : getGridClasses(columns, stackOnMobile)

  return (
    <div className={`grid ${gridClass} ${gapClasses[gap]}`}>
      {columnArray.map((index) => (
        <div
          key={index}
          className={`min-h-[100px] ${getColSpan(index)}`}
        >
          <DropZone zone={`column-${index}`} />
        </div>
      ))}
    </div>
  )
}

export const ColumnsConfig: ComponentConfig<ColumnsProps> = {
  label: 'Kolom',
  defaultProps: {
    columns: 2,
    gap: 'medium',
    distribution: 'equal',
    stackOnMobile: true,
  },
  fields: {
    columns: {
      type: 'select',
      label: 'Jumlah Kolom',
      options: [
        { label: '2 Kolom', value: 2 },
        { label: '3 Kolom', value: 3 },
        { label: '4 Kolom', value: 4 },
      ],
    },
    gap: {
      type: 'select',
      label: 'Jarak',
      options: [
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
      ],
    },
    distribution: {
      type: 'select',
      label: 'Distribusi (hanya 2 kolom)',
      options: [
        { label: 'Sama Rata', value: 'equal' },
        { label: 'Kiri Lebih Besar', value: 'left-heavy' },
        { label: 'Kanan Lebih Besar', value: 'right-heavy' },
      ],
    },
    stackOnMobile: {
      type: 'radio',
      label: 'Stack di Mobile',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
  },
  render: Columns,
}
